/**
 * @file Main permissions higher order component.
 *
 * The component handles all redirection logic based on permissions.
 *
 * Internally it holds a hashmap of all permissions each one declared by an arbitrary name
 * (i.e. "unlocked" if a user is considered unlocked).
 *
 * Every permission requires a `rule` property which gets evaluated on render and
 * if it returns false the component redirects to the `redirectTo` string declared
 * on the rule, i.e. a permission looks like this:
 *
 * ```.
 * unlocked: {
 *  rule: user.active && user.email_verified,
 *  redirectTo: '/app/${basename}/welcome',
 * },
 * ```.
 *
 * `basename` is the basename of each product and is automatically picked up by the route.
 * Whether the user has access to the basename is determined by its `roles` param
 * on the basename's definitions {@see role-definitions.js -> `definitions`}.
 *
 * Permissions are always evaluated top to bottom and the first one takes highest priority.
 * This means that if you pass in as `requirements` ['unlocked', 'account-complete']
 * it will stop and redirect if the user is not considered unlocked without checking
 * account complete.
 *
 * The component is curried which allows for greater versatility in creating different
 * permission level HOCs, i.e.:
 *
 * ```
 * const RequireAccount = RequirePermissions(['account-complete']);
 * const RequireAccountAndUnlock = RequirePermissions(
 *   ['account-complete', 'unlocked'],
 * );
 * ```
 *
 * You can then wrap any component you wish to add permissions to:
 *
 * ```
 * RequireAccountAndUnlock(SettingsPage);
 * ```
 *
 * Now `SettingsPage` will not be displayed in users that 1. don't have an account, 2. are not unlocked
 *
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect, withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import qs from 'query-string';

import { MeQuery } from '../../graphql/queries/account';
import RequireAuth from './RequireAuth';
import ACCOUNT_ROLES from '../../constants/account-roles';
import {
  getProductBasenames,
  getRolesByBasename,
} from '../../util/role-definitions';
import withBasename from './withBasename';
import { isProAccount } from '../../util/account';

/**
 * Higher order component.
 *
 * @param {Array} requirements - Onboarding requirements.
 * @param {React.Component} WrappedComponent - The component to be composed.
 * @returns {React.Component} - The composed component.
 */
const WithPermissionsHOC = (requirements, WrappedComponent) =>
  class extends Component {
    static propTypes = {
      user: PropTypes.shape({
        contact_phone: PropTypes.string,
        active: PropTypes.bool,
        email_verified: PropTypes.bool,
        roles: PropTypes.array,
      }),
      authenticated: PropTypes.bool,
      basename: PropTypes.oneOf(getProductBasenames()),
      staticContext: PropTypes.object,
    };

    getPermissions = props => {
      const { basename, user, onboarding } = props;

      return {
        'account-complete': {
          rule: !!user.contact_phone,
          redirectTo: '/auth/account-setup',
        },
        'email-verified': {
          rule: user.email_verified,
          redirectTo: `/app/${basename}/welcome`,
        },
        'pro-email-verified': {
          rule: user.email_verified || !isProAccount(user),
          redirectTo: `/app/${basename}/email-verification`,
        },
        'consumer-verified': {
          rule:
            isProAccount(user) || !!user.phone_number || user.email_verified,
          redirectTo: `/app/${basename}/email-verification`,
        },
        active: {
          rule: user.active || user.contributor_assignments?.total > 0,
          redirectTo: `/app/${basename}/welcome`,
        },
        unlocked: {
          rule:
            user.active && user.email_verified && onboarding === 'completed',
          redirectTo: `/app/${basename}/welcome`,
        },
      };
    };

    getRedirection = redirectMatch => {
      const { location, staticContext } = this.props;

      if (redirectMatch.external) {
        if (window.location) {
          window.location.assign(redirectMatch.redirectTo);
        }
        return null;
      }

      // Set referrer so that we can redirect the user
      // at the previously requested page (server side)
      // This is then caught on server's `context` (provided by
      // StaticRouter and passed along to Express's redirect handler
      // (see `react-server-renderer.js`)
      if (staticContext) {
        staticContext.referrer = location;
        staticContext.status = 401;
      }

      const redirectObject =
        staticContext || !location
          ? redirectMatch.redirectTo
          : {
              pathname: redirectMatch.redirectTo,
              search: `?${qs.stringify({
                next: `${location?.pathname}${location?.search}`,
              })}`,
            };

      return <Redirect to={redirectObject} />;
    };

    /**
     * Checks if the component has the required data to determine
     * permisssions. E.g. If the user and the has been fetched.
     *
     * @returns {boolean}
     */
    canDeterminePermissions = () => {
      const { user } = this.props;

      return !!user;
    };

    render() {
      const { basename, user } = this.props;
      const roles = getRolesByBasename(basename);

      // Skip the entire tree if we don't have enough
      // data to determine permissions
      if (!this.canDeterminePermissions()) {
        return null;
      }

      // Redirect the user if they don't have the required supplied role.
      // Admins get a free pass everywhere.
      if (
        !roles.some(role => user.roles.includes(role)) &&
        !user.roles.includes(ACCOUNT_ROLES.ADMINISTRATOR)
      ) {
        return <Redirect to="/app" />;
      }

      const permissions = this.getPermissions(this.props);

      // Find the first matching permission with a false `rule`
      // it will be our redirect
      const redirectMatch = requirements.reduceRight((acc, permission) => {
        const perm = permissions[permission];

        if (!perm) {
          return acc;
        }

        return perm.rule ? acc : perm;
      }, undefined);

      return redirectMatch ? (
        this.getRedirection(redirectMatch)
      ) : (
        <WrappedComponent {...this.props} />
      );
    }
  };

/**
 * Base RequirePermissions component.
 *
 * @param {Array} requirements - Onboarding requirements.
 * @returns {Function} - Higher order container which will decorate the passed in component with all required data for the permissions functionality.
 */
const RequirePermissions = requirements => WrappedComponent => {
  const WithPermissions = WithPermissionsHOC(requirements, WrappedComponent);

  const withUserData = graphql(MeQuery, {
    props: ({ data }) => ({
      loading: data.loading,
      error: data.error,
      user: data.me,
      onboarding: data.me?.onboarding?.data?.pro_landing,
    }),
    skip: ownProps => !ownProps.authenticated,
  });

  return compose(
    withRouter,
    RequireAuth,
    withBasename,
    withUserData,
  )(WithPermissions);
};

export { WithPermissionsHOC };
export default RequirePermissions;
